import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.API_URL;

// Keep StepProgress and Step components as before

// Step Progress Indicator (keep as is)
const StepProgress = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;

        return (
          <React.Fragment key={stepNumber}>
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 ${
                isCompleted
                  ? 'bg-green-500 text-white'
                  : isActive
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {isCompleted ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                stepNumber
              )}
            </div>
            {stepNumber < totalSteps && (
              <div
                className={`w-16 h-1 mx-2 transition-all duration-300 ${
                  stepNumber < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}
              ></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Step Component (keep as is)
const Step = ({ children, isVisible, direction = 'right' }) => {
  return (
    <div
      className={`transition-all duration-500 ease-in-out ${
        isVisible
          ? 'opacity-100 transform translate-x-0'
          : direction === 'right'
          ? 'opacity-0 transform translate-x-full'
          : 'opacity-0 transform -translate-x-full'
      } ${!isVisible ? 'absolute inset-0 pointer-events-none' : ''}`}
    >
      {children}
    </div>
  );
};

const InvitationAcceptance = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState('right');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  });

  const validatePassword = (password: string) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(password)) return 'Must contain lowercase letter';
    if (!/[A-Z]/.test(password)) return 'Must contain uppercase letter';
    if (!/\d/.test(password)) return 'Must contain number';
    if (!/[@$!%*?&]/.test(password)) return 'Must contain special character (@$!%*?&)';
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate
    const errors = {
      name: formData.name.trim() ? '' : 'Name is required',
      password: validatePassword(formData.password),
      confirmPassword: formData.password !== formData.confirmPassword ? 'Passwords do not match' : '',
    };

    setFormErrors(errors);
    if (Object.values(errors).some((err) => err)) return;

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/accept-invitation`, {
        token,
        password: formData.password,
        name: formData.name,
      });

      localStorage.setItem('_jwt_fto', response.data.access_token);

      setDirection('right');
      setCurrentStep(2);

      setTimeout(() => {
        navigate('/app/onboarding');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to accept invitation. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="bg-gray-200 px-4 py-2 rounded text-gray-600 font-medium">LOGO</div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to FreightTech Platform</h1>
        <p className="text-xl text-gray-600">Set up your account to get started</p>
      </div>

      {/* Steps */}
      <div className="max-w-4xl mx-auto px-6 pb-16">
        <StepProgress currentStep={currentStep} totalSteps={2} />

        <div className="bg-white rounded-lg shadow-lg min-h-[500px] relative overflow-hidden">
          {/* Step 1: Setup */}
          <Step isVisible={currentStep === 1} direction={direction}>
            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Set Up Your Account</h2>
                <p className="text-gray-600 text-lg">Create your password to secure your account</p>
              </div>

              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Jan Kowalski"
                    />
                    {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                      {formErrors.password && <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>}
                      <p className="mt-2 text-xs text-gray-500">
                        8+ characters, uppercase, lowercase, number, special character
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="••••••••"
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isLoading ? 'Setting up...' : 'Complete Setup'}
                  </button>
                </div>
              </form>
            </div>
          </Step>

          {/* Step 2: Success */}
          <Step isVisible={currentStep === 2} direction={direction}>
            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Created Successfully!</h2>
                <p className="text-gray-600 text-lg">Redirecting to your dashboard...</p>
              </div>

              <div className="max-w-md mx-auto">
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-900 font-medium mb-4">Welcome to FreightTech Platform!</p>
                </div>
              </div>
            </div>
          </Step>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Operations?</h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Join logistics professionals saving time with automated workflows.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptance;
